import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { getClientes, Cliente } from '../services/clienteService';
import { getLaboratorios, Laboratorio } from '../services/laboratorioService';
import { Button, Card, CardHeader, Checkbox, FormField, Input, Modal, Select } from './ui';

export type TipoUsuario = 'cliente' | 'laboratorio' | 'administrador';

/** Organización a la que queda asociado el usuario, si se elige una existente. */
export interface OrganizacionInicial {
  tipo: 'cliente' | 'laboratorio';
  id: number;
}

interface CrearUsuarioFormProps {
  onUsuarioCreado?: () => void;
  /** Preselección al entrar desde la ficha de un cliente o laboratorio. */
  organizacionInicial?: OrganizacionInicial;
}

interface UsuarioFormData {
  username: string;
  nombre: string;
  apellido: string;
  email: string;
  tipo: TipoUsuario;
  // Datos de la organización (cliente o laboratorio). El nombre de la
  // organización es independiente del nombre de la persona que la opera.
  nombre_organizacion?: string;
  email_organizacion?: string;
  razon_social?: string;
  cuit?: string;
  telefono?: string;
  direccion?: string;
  ciudad?: string;
  provincia?: string;
}

/**
 * Permisos que recibe cada tipo de usuario. Para clientes y laboratorios son
 * fijos: se muestran para que quede claro qué va a poder hacer la cuenta, pero
 * no se editan al darla de alta.
 */
const PERMISOS_FIJOS: Record<'cliente' | 'laboratorio', Record<string, boolean>> = {
  cliente: {
    p_busqueda: true,
    p_carga: false,
    p_modif_analisis: true,
    p_nuevo_analisis: true,
  },
  laboratorio: {
    p_busqueda: true,
    p_carga: true,
    p_modif_analisis: true,
    p_nuevo_analisis: false,
  },
};

const ETIQUETAS_PERMISO: Record<string, string> = {
  p_busqueda: 'Búsqueda',
  p_carga: 'Carga de resultados',
  p_modif_analisis: 'Modificar análisis',
  p_nuevo_analisis: 'Nuevo análisis',
};

const datosIniciales: UsuarioFormData = {
  username: '',
  nombre: '',
  apellido: '',
  email: '',
  tipo: 'cliente',
  nombre_organizacion: '',
  email_organizacion: '',
  razon_social: '',
  cuit: '',
  telefono: '',
  direccion: '',
  ciudad: '',
  provincia: '',
};

const permisosAdminIniciales = {
  p_busqueda: false,
  p_carga: false,
  p_modif_analisis: false,
  p_nuevo_analisis: false,
  p_g_ensayos: false,
  p_g_usuarios: false,
};

const CrearUsuarioForm: React.FC<CrearUsuarioFormProps> = ({ onUsuarioCreado, organizacionInicial }) => {
  const [formData, setFormData] = useState<UsuarioFormData>({
    ...datosIniciales,
    tipo: organizacionInicial?.tipo ?? 'cliente',
  });
  const [permisosAdmin, setPermisosAdmin] = useState(permisosAdminIniciales);

  // Una cuenta puede sumarse a un cliente o laboratorio que ya existe, en lugar
  // de crear la organización otra vez.
  const [usaExistente, setUsaExistente] = useState(!!organizacionInicial);
  const [organizacionId, setOrganizacionId] = useState<number | ''>(organizacionInicial?.id ?? '');
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [laboratorios, setLaboratorios] = useState<Laboratorio[]>([]);
  const [cargandoOrganizaciones, setCargandoOrganizaciones] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultado, setResultado] = useState<{
    username: string;
    email: string;
    password?: string | null;
    emailSent: boolean;
  } | null>(null);

  const esOrganizacion = formData.tipo === 'cliente' || formData.tipo === 'laboratorio';

  const cargarOrganizaciones = useCallback(async () => {
    setCargandoOrganizaciones(true);
    try {
      const [datosClientes, datosLaboratorios] = await Promise.all([getClientes(), getLaboratorios()]);
      setClientes(datosClientes);
      setLaboratorios(datosLaboratorios);
    } catch (err) {
      console.error('Error al cargar clientes y laboratorios:', err);
    } finally {
      setCargandoOrganizaciones(false);
    }
  }, []);

  useEffect(() => {
    cargarOrganizaciones();
  }, [cargarOrganizaciones]);

  // Al elegir una organización existente sus datos se muestran completos y
  // bloqueados: se está sumando un usuario, no editando la empresa.
  const organizacionElegida: (Cliente | Laboratorio) | undefined =
    organizacionId === ''
      ? undefined
      : formData.tipo === 'cliente'
        ? clientes.find(c => c.id === Number(organizacionId))
        : laboratorios.find(l => l.id === Number(organizacionId));

  const valorOrganizacion = (campo: keyof UsuarioFormData, campoOrg: string): string => {
    if (usaExistente) {
      const dato = (organizacionElegida as any)?.[campoOrg];
      return dato === undefined || dato === null ? '' : String(dato);
    }
    return (formData[campo] as string) || '';
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermisoAdmin = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPermisosAdmin(prev => ({ ...prev, [name]: checked }));
  };

  const handleTipoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tipo = e.target.value as TipoUsuario;
    setFormData(prev => ({ ...prev, tipo }));
    // La organización elegida pertenece a un tipo: al cambiarlo se descarta.
    setOrganizacionId('');
    if (tipo === 'administrador') setUsaExistente(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (esOrganizacion && usaExistente && organizacionId === '') {
      setError(
        formData.tipo === 'cliente'
          ? 'Elija el cliente al que se suma el usuario.'
          : 'Elija el laboratorio al que se suma el usuario.'
      );
      return;
    }

    setLoading(true);
    setResultado(null);

    try {
      const { username, email } = formData;
      const permisos =
        formData.tipo === 'administrador'
          ? {
              ...permisosAdmin,
              // Gestión de alertas y de configuración no se eligen en pantalla:
              // todo administrador las tiene.
              p_g_alertas: true,
              p_g_config: true,
            }
          : PERMISOS_FIJOS[formData.tipo];

      const datos: Record<string, unknown> = { ...formData, ...permisos };
      if (esOrganizacion && usaExistente) {
        // Con `id_grupo` el backend asocia la cuenta a la organización existente
        // y no vuelve a crearla.
        datos.id_grupo = Number(organizacionId);
        delete datos.nombre_organizacion;
        delete datos.email_organizacion;
        delete datos.razon_social;
        delete datos.cuit;
        delete datos.telefono;
        delete datos.direccion;
        delete datos.ciudad;
        delete datos.provincia;
      }

      const response = await api.post('/usuarios/crear', datos);
      setResultado({
        username,
        email,
        password: response.data.generatedPass,
        emailSent: !!response.data.emailSent,
      });
      setFormData({ ...datosIniciales, tipo: formData.tipo });
      setPermisosAdmin(permisosAdminIniciales);
      if (!organizacionInicial) {
        setUsaExistente(false);
        setOrganizacionId('');
      }
      cargarOrganizaciones();

      // Notificar al componente padre para actualizar la gestión
      if (onUsuarioCreado) {
        onUsuarioCreado();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al crear el usuario');
    } finally {
      setLoading(false);
    }
  };

  const renderCamposComunes = () => {
    if (!esOrganizacion) return null;

    const esCliente = formData.tipo === 'cliente';
    const titulo = esCliente ? 'Información del Cliente' : 'Información del Laboratorio';
    const etiquetaNombre = esCliente ? 'Nombre del cliente' : 'Nombre del laboratorio';
    const opciones = esCliente ? clientes : laboratorios;

    return (
      <Card>
        <CardHeader
          title={titulo}
          description="Datos de la organización. Son independientes de los datos personales del usuario."
        />

        <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="radio"
                name="modo-organizacion"
                className="h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                checked={!usaExistente}
                onChange={() => {
                  setUsaExistente(false);
                  setOrganizacionId('');
                }}
              />
              {esCliente ? 'Crear un cliente nuevo' : 'Crear un laboratorio nuevo'}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="radio"
                name="modo-organizacion"
                className="h-4 w-4 border-slate-300 text-brand-600 focus:ring-brand-500"
                checked={usaExistente}
                onChange={() => setUsaExistente(true)}
              />
              {esCliente ? 'Agregar a un cliente existente' : 'Agregar a un laboratorio existente'}
            </label>
          </div>

          {usaExistente && (
            <FormField
              label={esCliente ? 'Cliente' : 'Laboratorio'}
              htmlFor="organizacion-existente"
              className="mt-4"
              required
            >
              <Select
                id="organizacion-existente"
                value={organizacionId}
                onChange={e => setOrganizacionId(e.target.value === '' ? '' : Number(e.target.value))}
                required
              >
                <option value="">{esCliente ? 'Seleccionar cliente' : 'Seleccionar laboratorio'}</option>
                {opciones.map(opcion => (
                  <option key={opcion.id} value={opcion.id}>
                    {opcion.nombre}
                    {(opcion as any).razon_social ? ` — ${(opcion as any).razon_social}` : ''}
                  </option>
                ))}
              </Select>
              {cargandoOrganizaciones && (
                <span className="mt-1 block text-sm text-slate-500">Cargando...</span>
              )}
            </FormField>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={etiquetaNombre} htmlFor="camposComunes-nombre" required={!usaExistente}>
            <Input
              id="camposComunes-nombre"
              type="text"
              name="nombre_organizacion"
              value={valorOrganizacion('nombre_organizacion', 'nombre')}
              onChange={handleInputChange}
              disabled={usaExistente}
              required={!usaExistente}
            />
          </FormField>

          <FormField label="CUIT" htmlFor="camposComunes-cuit" required={!usaExistente}>
            <Input
              id="camposComunes-cuit"
              type="text"
              name="cuit"
              value={valorOrganizacion('cuit', 'cuit')}
              onChange={handleInputChange}
              placeholder="XX-XXXXXXXX-X"
              disabled={usaExistente}
              required={!usaExistente}
            />
          </FormField>

          <FormField label="Razón Social" htmlFor="camposComunes-razon_social" required={!usaExistente}>
            <Input
              id="camposComunes-razon_social"
              type="text"
              name="razon_social"
              value={valorOrganizacion('razon_social', 'razon_social')}
              onChange={handleInputChange}
              disabled={usaExistente}
              required={!usaExistente}
            />
          </FormField>

          <FormField label="Dirección" htmlFor="camposComunes-direccion" required={!usaExistente}>
            <Input
              id="camposComunes-direccion"
              type="text"
              name="direccion"
              value={valorOrganizacion('direccion', 'direccion')}
              onChange={handleInputChange}
              disabled={usaExistente}
              required={!usaExistente}
            />
          </FormField>

          <FormField label="Provincia" htmlFor="camposComunes-provincia" required={!usaExistente}>
            <Input
              id="camposComunes-provincia"
              type="text"
              name="provincia"
              value={valorOrganizacion('provincia', 'provincia')}
              onChange={handleInputChange}
              disabled={usaExistente}
              required={!usaExistente}
            />
          </FormField>

          <FormField label="Teléfono" htmlFor="camposComunes-telefono" required={!usaExistente}>
            <Input
              id="camposComunes-telefono"
              type="text"
              name="telefono"
              value={valorOrganizacion('telefono', 'telefono')}
              onChange={handleInputChange}
              disabled={usaExistente}
              required={!usaExistente}
            />
          </FormField>

          <FormField label="Ciudad" htmlFor="camposComunes-ciudad" required={!usaExistente}>
            <Input
              id="camposComunes-ciudad"
              type="text"
              name="ciudad"
              value={valorOrganizacion('ciudad', 'ciudad')}
              onChange={handleInputChange}
              disabled={usaExistente}
              required={!usaExistente}
            />
          </FormField>

          <FormField
            label={esCliente ? 'Email del cliente' : 'Email del laboratorio'}
            htmlFor="camposComunes-email"
            required={!usaExistente}
          >
            <Input
              id="camposComunes-email"
              type="email"
              name="email_organizacion"
              value={valorOrganizacion('email_organizacion', 'email')}
              onChange={handleInputChange}
              disabled={usaExistente}
              required={!usaExistente}
            />
          </FormField>
        </div>
      </Card>
    );
  };

  const renderPermisos = () => {
    if (formData.tipo === 'administrador') {
      return (
        <Card>
          <CardHeader title="Permisos de Administrador" />

          <h4 className="text-sm font-semibold text-slate-700 mb-3">Permisos operativos</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox label="Búsqueda" name="p_busqueda" checked={permisosAdmin.p_busqueda} onChange={handlePermisoAdmin} />
            <Checkbox label="Carga" name="p_carga" checked={permisosAdmin.p_carga} onChange={handlePermisoAdmin} />
            <Checkbox label="Modificar Análisis" name="p_modif_analisis" checked={permisosAdmin.p_modif_analisis} onChange={handlePermisoAdmin} />
            <Checkbox label="Nuevo Análisis" name="p_nuevo_analisis" checked={permisosAdmin.p_nuevo_analisis} onChange={handlePermisoAdmin} />
          </div>

          <hr className="my-6 border-t border-slate-300" />

          <h4 className="text-sm font-semibold text-slate-700 mb-3">Permisos de gestión</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Checkbox label="Gestionar Ensayos" name="p_g_ensayos" checked={permisosAdmin.p_g_ensayos} onChange={handlePermisoAdmin} />
            <Checkbox label="Gestionar Usuarios" name="p_g_usuarios" checked={permisosAdmin.p_g_usuarios} onChange={handlePermisoAdmin} />
          </div>
        </Card>
      );
    }

    const fijos = PERMISOS_FIJOS[formData.tipo];
    const habilitados = Object.keys(fijos).filter(clave => fijos[clave]);

    return (
      <Card>
        <CardHeader
          title="Permisos"
          description={
            formData.tipo === 'cliente'
              ? 'Un cliente solicita análisis y consulta sus resultados; no carga resultados.'
              : 'Un laboratorio carga los resultados de los análisis que recibe; no genera análisis nuevos.'
          }
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {habilitados.map(clave => (
            <Checkbox key={clave} label={ETIQUETAS_PERMISO[clave]} checked readOnly disabled />
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-500">
          Estos permisos son los que corresponden al tipo de usuario y no se modifican al crear la cuenta.
          Después se ajustan desde la ficha del {formData.tipo === 'cliente' ? 'cliente' : 'laboratorio'} en Gestión.
        </p>
      </Card>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      {resultado && (
        <Modal
          title={resultado.emailSent ? 'Usuario creado' : 'Usuario creado, sin correo'}
          onClose={() => setResultado(null)}
          size="sm"
          footer={
            <div className="flex justify-end">
              <Button onClick={() => setResultado(null)}>Aceptar</Button>
            </div>
          }
        >
          {resultado.emailSent ? (
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div className="text-sm text-slate-700">
                <p>
                  El usuario <code className="font-mono font-semibold">{resultado.username}</code> se creó
                  correctamente.
                </p>
                <p className="mt-2">
                  Se envió un correo a <strong>{resultado.email}</strong> con la contraseña de acceso. Es una
                  contraseña temporal: el sistema le pedirá que defina una propia la primera vez que ingrese.
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Si no encuentra el mensaje, indíquele que revise la carpeta de correo no deseado. Desde la
                  lista de usuarios puede generar una contraseña nueva y volver a enviarla.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-500 flex-shrink-0" />
              <div className="text-sm text-slate-700">
                <p>El usuario se creó correctamente, pero no se pudo enviar el correo.</p>
                <p className="mt-2">Entregue estos datos de acceso al usuario:</p>
                <div className="mt-2">Usuario: <code className="font-mono font-semibold">{resultado.username}</code></div>
                <div className="mt-1">Contraseña temporal: <code className="font-mono font-semibold">{resultado.password}</code></div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => navigator.clipboard.writeText(resultado.username + ' ' + resultado.password)}
                >
                  Copiar datos de acceso
                </Button>
              </div>
            </div>
          )}
        </Modal>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información básica del usuario */}
        <Card>
          <CardHeader title="Información Básica del Usuario" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Nombre de Usuario" htmlFor="username" required>
              <Input id="username" type="text" name="username" value={formData.username} onChange={handleInputChange} required />
            </FormField>

            <FormField label="Nombre" htmlFor="nombre" required>
              <Input id="nombre" type="text" name="nombre" value={formData.nombre} onChange={handleInputChange} required />
            </FormField>

            <FormField label="Apellido" htmlFor="apellido" required>
              <Input id="apellido" type="text" name="apellido" value={formData.apellido} onChange={handleInputChange} required />
            </FormField>

            <FormField label="Email" htmlFor="email" required>
              <Input id="email" type="email" name="email" value={formData.email} onChange={handleInputChange} required />
            </FormField>

          </div>

          <div className="mt-6 rounded-lg border-2 border-brand-300 bg-brand-50 p-4">
            <FormField label="Tipo de Usuario" htmlFor="tipo" required>
              <Select
                id="tipo"
                name="tipo"
                value={formData.tipo}
                onChange={handleTipoChange}
                disabled={!!organizacionInicial}
                required
              >
                <option value="cliente">Cliente</option>
                <option value="laboratorio">Laboratorio</option>
                <option value="administrador">Administrador</option>
              </Select>
            </FormField>
          </div>
        </Card>

        {/* Campos específicos según el tipo */}
        {renderCamposComunes()}
        {renderPermisos()}

        {/* Mensajes de error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Botón de envío */}
        <div className="flex justify-end">
          <Button type="submit" loading={loading}>
            {loading ? 'Creando...' : 'Crear Usuario'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CrearUsuarioForm;
